/**
 * Provides an interface for viewing and manipulating properties of a given set of map-related objects. ESRI features, form responses, and un-committed map objects are all supported.
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * layer-create | The `MapServiceBase` of the layer created and the `MapLayerBase` representing the layer that was created. | Fires whenever a new layer is successfully created through an interaction with this interface.
 * service-create | The `MapServiceBase` representing the service that was created. | Fires whenever a new service is successfully created through an interaction with this interface.
 * select | The object selected and the index in the original result list that was selected. | Fires whenever a different object in the set of results is selected.
 * locate | The target `esri.geometry.Geometry`. | Fires whenever the user elects to pan to the given geometry.
 * zoom | The target `esri.geometry.Geometry`. | Fires whenever the user elects to zoom to the given geometry.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * pager | The `PagerView` used to navigate between results.
 * @module app/views/map/ResultsPanelView
 * @extends {MapDetailView}
 */
import Backbone from "backbone";
import MapDetailView from "app/views/map/MapDetailView";
import _ from "underscore";
import PagerView from "core/views/PagerView";
import ObjectPanelView from "app/views/map/ObjectPanelView";
import ButtonView from "core/views/ButtonView";
import Operation from "core/models/Operation";
import ErrorUtils from "app/utils/ErrorUtils";
import mapContext from "app/views/map/MapContext";
var ResultsPanelView = MapDetailView.extend(
  {
    className: "detail-view results-panel",

    /**
     * Initializes the view.
     * @param  {object} options The options hash.
     * @param {string} options.title:'Search Results' The title for the view.
     *
     */
    initialize: function (options) {
      MapDetailView.prototype.initialize.apply(this, arguments);
      /*Other options used: model, mapObject, question*/
      this.options = _.defaults({}, options, {
        title: "Properties",
      });
      this.pager = new PagerView({
        count: mapContext.getSelection().length,
      });

      this.setTitle(this.options.title);
    },

    _renderObject: function (model, layer) {
      var view = new ObjectPanelView({
        model: model,
        layer: layer,
      });

      view.render();

      return view;
    },

    _renderEmpty: function (model) {
      var View = ResultsPanelView.EmptyView;
      var view = new View({
        model: model,
      });

      view.render();

      return view;
    },

    render: function () {
      var results = mapContext.getSelection();
      var self = this;

      if (results.length) {
        this._renderItem(0);
        this.pager.render();

        if (results.length <= 1) {
          this.setFooter("");
        } else {
          this.setFooter(this.pager.$el);
        }

        self.pager.on(
          "select",
          function (ordinal) {
            var idx = ordinal - 1;

            self._renderItem(idx);

            mapContext
              .getOperation("clear-selection")
              .action()
              .then(function () {
                return mapContext
                  .getOperation("highlight-geometry")
                  .action([results.at(idx)]);
              })
              .fail(
                ErrorUtils.errorHandler({
                  activity: "selecting the item",
                })
              );

            self.pager.delegateEvents();
          },
          self
        );
      } else {
        this.setFooter("");
        this.$el.html("No items selected.");
      }
    },

    _renderItem: function (idx) {
      var results = mapContext.getSelection();
      var model = results.at(idx);

      this.$el.html("");
      var detail;

      if (model) {
        //this could be coming from the workspace layer, an internal object layer, or an external object layer
        //need to coordinate feature/feature, mapobject/graphic, and response/mapobject pairings for the types of layers

        var layer = model.originalLayer;

        if (layer.serviceLayer) {
          //not workspace
          detail = this._renderObject(model, layer);
        } else {
          //it's a workspace object - no fields associated
          detail = this._renderEmpty(model, layer);
        }

        this.$el.append(detail.$el);
      }

      this.refreshControls();
    },

    refreshControls: function () {
      this.clearControls();

      var selection = mapContext.getSelection(),
        region = mapContext.getSearchRegion(),
        model = selection.at(this.pager.current - 1);

      if (model) {
        this.addControl(
          this.createIconButton(
            mapContext.getOperation("zoom-to-geometry").withModel([model])
          )
        );
        this.addControl(
          this.createIconButton(
            mapContext.getOperation("move-geometry").withModel([model])
          )
        );
      }

      this.addControl(
        this.createIconButton(
          mapContext.getOperation("pan-to-geometry").withModel(selection)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext
            .getOperation("print-geometry")
            .wrap(function (model) {
              if (region.length) {
                var regionVisible = region.isVisible();

                region.hide();

                mapContext.operations.once("print-geometry", function () {
                  if (regionVisible) {
                    region.show();
                  }
                });
              }

              return Operation.resolve(model);
            })
            .withModel(selection)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("export-geometry-csv").withModel(selection)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("export-geometry-table").withModel(selection)
        )
      );
    },
  },
  {
    EmptyView: Backbone.View.extend({
      className: "new-object-view",

      initialize: function (options) {
        this.model = options.model;
      },

      render: function () {
        var self = this;
        var createButton = new ButtonView({
          type: "primary",
          text: "Add to Layer",
          click: function () {
            mapContext
              .getOperation("move-geometry")
              .action([self.model])
              .fail(
                ErrorUtils.errorHandler({
                  activity: "saving the geometry",
                })
              );
          },
        });
        var removeButton = new ButtonView({
          type: "danger",
          text: "Remove",
          click: function () {
            mapContext
              .getOperation("remove-geometry")
              .action([self.model])
              .fail(
                ErrorUtils.errorHandler({
                  activity: "removing the geometry",
                })
              );
          },
        });

        createButton.render();
        removeButton.render();
        this.$el.append("<div>Unsaved shape</div>");
        this.$el.append(createButton.$el);
        this.$el.append(removeButton.$el);
      },
    }),
  }
);

export default ResultsPanelView;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/ResultsPanelView.js
// module id = 945
// module chunks = 1